import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { getBranchesAll } from '../api/dms/branch.api';
import { logConsole, logTransaction } from '../common/log';
import { handleThunkError } from '../common/util';
import { IBranch, IBranchSyncStatus } from '../models/dms/branch.model';
import { LogType } from '../models/log.model';
import { IOdataResult } from '../models/odata.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { setIsLoadingCountState } from './appSlice';

interface IBranchSyncState {
  branchesSync?: IBranchSyncStatus;
  branchesSyncLoading: boolean;
}

const initialState: IBranchSyncState = {
  branchesSync: {
    hasSync: false,
    timestamp: 0,
    isProcessing: false,
    data: {} as IOdataResult<IBranch>
  },
  branchesSyncLoading: false,
};

/// THUNKS

export const setBranchesSyncStoreAsync = createAsyncThunk<{}, IBranchSyncStatus | undefined>(
  'branchSync/setBranchesSyncStoreAsync',
  async (val, { rejectWithValue, dispatch }) => {
    logTransaction(LogType.Info, `Setting branches to storage`, '[branchSync] setBranchesSyncStoreAsync');
    try {
      dispatch(setBranchesSyncState(val));
      await updateConfigObject<IBranchSyncStatus>(ConfigName.BranchesSync, val || {} as IBranchSyncStatus, false);
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const setBranchesProcessingStoreAsync = createAsyncThunk<{}, boolean>(
  'branchSync/setBranchesProcessingStoreAsync',
  async (val, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, '', '[branchSync] setBranchesProcessingStoreAsync');
    try {
      dispatch(setBranchesProcessingState(val));
      await updateConfigObject<IBranchSyncStatus>(ConfigName.BranchesSync, {
        ...(getState() as RootState).branchSync.branchesSync,
        isProcessing: val
      } as IBranchSyncStatus, false);
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const setBranchesHasSyncStoreAsync = createAsyncThunk<{}, boolean>(
  'branchSync/setBranchesHasSyncStoreAsync',
  async (val, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, '', '[branchSync] setBranchesHasSyncStoreAsync');
    try {
      dispatch(setBranchesHasSyncState(val));
      await updateConfigObject<IBranchSyncStatus>(ConfigName.BranchesSync, {
        ...(getState() as RootState).branchSync.branchesSync,
        hasSync: val,
        isProcessing: false,
      } as IBranchSyncStatus, false);
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const fetchBranchesStoreAsync = createAsyncThunk<{}, void>(
  'branchSync/fetchBranchesStoreAsync',
  async (nil, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Fetching branches from database`, '[branchSync] fetchBranchesStoreAsync');
    try {
      dispatch(setIsLoadingCountState(1));
      dispatch(setBranchesSyncLoadingState(true));

      const state = (getState() as RootState);
      const { branchesSync } = state.branchSync;

      let branchesSyncState = { ...branchesSync } as IBranchSyncStatus;

      try {
        const responseData = await getBranchesAll();

        branchesSyncState = {
          ...branchesSyncState,
          timestamp: new Date().getTime(),
          hasSync: true,
          isProcessing: false,
          data: { ...responseData }
        };

        dispatch(setBranchesSyncState(branchesSyncState));
        await updateConfigObject<IBranchSyncStatus>(ConfigName.BranchesSync, branchesSyncState, false);
        return responseData;
      } catch (ex: any) {
        logTransaction(LogType.Error, `Failed to execute fetchBranchesStoreAsync thunk. ${ex?.message}`);
      } finally {
        dispatch(setIsLoadingCountState(-1));
        dispatch(setBranchesSyncLoadingState(false));
      }

    } catch (err: any) {
      return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setBranchesSyncLoadingState(false));
    }
  }
);

/// SLICE

export const branchSyncSlice = createSlice({
  name: 'branchSync',
  initialState,
  reducers: {
    setBranchesSyncState: (state, action: PayloadAction<IBranchSyncStatus | undefined>) => {
      logConsole(LogType.Info, '[branchSync] setBranchesSyncState');
      state.branchesSync = action.payload;
    },
    setBranchesProcessingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[branchSync] setBranchesProcessingState: ${action.payload}`);
      if (state.branchesSync) {
        state.branchesSync.isProcessing = action.payload;
      }
    },
    setBranchesHasSyncState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[branchSync] setBranchesHasSyncState: ${action.payload}`);
      if (state.branchesSync) {
        state.branchesSync.hasSync = action.payload;
        state.branchesSync.isProcessing = false;
      }
    },
    setBranchesSyncLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[branchSync] setBranchesSyncLoadingState: ${action.payload}`);
      state.branchesSyncLoading = action.payload;
    },
  },
});

/// REDUCERS

export const { setBranchesSyncState, setBranchesProcessingState, setBranchesHasSyncState, setBranchesSyncLoadingState } = branchSyncSlice.actions;

export default branchSyncSlice.reducer;
